
import { useState, useEffect } from "react";
import { Customer, Group } from "@/types/profile";
import { mockCustomers, getGroupsByCustomerId, mockGroups } from "@/utils/customerGroups";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import CustomerTag from "@/components/CustomerTag";
import { UseFormReturn } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { getTagsByCustomerId, CustomerTagItem } from "@/utils/customerTags";

interface ProfileAssignmentProps {
  form: UseFormReturn<any>;
  setCustomers: (customers: string[]) => void;
  setGroups: (groups: string[]) => void;
  setTags?: (tags: string[]) => void;
  initialCustomers?: string[];
  initialGroups?: string[];
  initialTags?: string[];
  multipleCustomers?: boolean;
}

export const ProfileAssignment = ({
  form,
  setCustomers,
  setGroups,
  setTags,
  initialCustomers = [],
  initialGroups = [],
  initialTags = [],
  multipleCustomers = false,
}: ProfileAssignmentProps) => {
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>(initialCustomers);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [availableTags, setAvailableTags] = useState<CustomerTagItem[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>(initialGroups);
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags || []);
  const [tempCustomer, setTempCustomer] = useState<string>("");

  // Update available groups/tags when customers change
  useEffect(() => {
    if (selectedCustomers.length > 0) {
      // Get all groups for all selected customers
      const allGroups = selectedCustomers.flatMap((customerId) =>
        getGroupsByCustomerId(customerId)
      );

      // Filter out duplicates (if any)
      const uniqueGroups = allGroups.filter((group, index, self) =>
        index === self.findIndex((g) => g.id === group.id)
      );
      setAvailableGroups(uniqueGroups);

      // Tags
      const allTags = selectedCustomers.flatMap((customerId) =>
        getTagsByCustomerId(customerId)
      );
      const uniqueTags = allTags.filter((tag, index, self) =>
        index === self.findIndex((t) => t.id === tag.id)
      );
      setAvailableTags(uniqueTags);
    } else {
      setAvailableGroups([]);
      setSelectedGroups([]);
      setGroups([]);
      setAvailableTags([]);
      setSelectedTags([]);
      setTags?.([]);
    }
  }, [selectedCustomers, setGroups, setTags]);

  // Handle customer selection for single customer mode
  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomers([customerId]);
    setCustomers([customerId]);
  };

  // Handle customer selection for multiple customer mode
  const handleAddCustomer = (customerId: string) => {
    if (customerId && !selectedCustomers.includes(customerId)) {
      const newCustomers = [...selectedCustomers, customerId];
      setSelectedCustomers(newCustomers);
      setCustomers(newCustomers);
      setTempCustomer("");
    }
  };

  // Handle customer removal
  const handleRemoveCustomer = (customerId: string) => {
    const newCustomers = selectedCustomers.filter(id => id !== customerId);
    setSelectedCustomers(newCustomers);
    setCustomers(newCustomers);
  };

  // Handle group selection
  const handleGroupChange = (groupId: string, checked: boolean) => {
    const updatedGroups = checked
      ? [...selectedGroups, groupId]
      : selectedGroups.filter((id) => id !== groupId);

    setSelectedGroups(updatedGroups);
    setGroups(updatedGroups);
  };

  // Handle tag selection
  const handleTagChange = (tagId: string, checked: boolean) => {
    const updatedTags = checked
      ? [...selectedTags, tagId]
      : selectedTags.filter((id) => id !== tagId);

    setSelectedTags(updatedTags);
    setTags?.(updatedTags);
  };

  // Find customer by ID
  const getCustomerById = (id: string): Customer | undefined => {
    return mockCustomers.find((customer) => customer.id === id);
  };

  // Compute nesting depth for groups
  const getGroupDepth = (groupId: string): number => {
    const group = mockGroups.find((g) => g.id === groupId);
    let depth = 0;
    let current = group;
    while (current?.parentId) {
      depth++;
      current = mockGroups.find((g) => g.id === current?.parentId);
    }
    return depth;
  };

  return (
    <div className="space-y-6">
      <div>
        <FormLabel className="mb-2 block">Customer Assignment</FormLabel>
        
        {multipleCustomers ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  value={tempCustomer}
                  onValueChange={(value) => setTempCustomer(value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select customers to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockCustomers
                      .filter(customer => !selectedCustomers.includes(customer.id))
                      .map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          <div className="flex items-center">
                            <CustomerTag customer={customer.name} />
                            <span className="ml-2">{customer.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <FormControl>
                <button 
                  type="button" 
                  className="h-10 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md"
                  onClick={() => handleAddCustomer(tempCustomer)}
                  disabled={!tempCustomer}
                >
                  Add
                </button>
              </FormControl>
            </div>
            
            {selectedCustomers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedCustomers.map(customerId => {
                  const customer = getCustomerById(customerId);
                  if (!customer) return null;
                  
                  return (
                    <Badge key={customerId} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                      <CustomerTag customer={customer.name} />
                      <span>{customer.name}</span>
                      <button 
                        type="button" 
                        className="ml-1 hover:text-destructive"
                        onClick={() => handleRemoveCustomer(customerId)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <FormField
            control={form.control}
            name="customer"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Select
                    value={selectedCustomers[0] || ""}
                    onValueChange={(value) => {
                      handleCustomerChange(value);
                      field.onChange(value);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockCustomers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          <div className="flex items-center">
                            <CustomerTag customer={customer.name} />
                            <span className="ml-2">{customer.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {selectedCustomers.length > 0 && availableGroups.length > 0 && (
        <div className="space-y-2">
          <Label>Assign to Groups</Label>
          <ScrollArea className="h-[200px] border rounded-md p-4">
            <div className="space-y-2">
              {availableGroups.map((group) => {
                const depth = getGroupDepth(group.id);
                return (
                  <div key={group.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={group.id}
                      checked={selectedGroups.includes(group.id)}
                      onCheckedChange={(checked: boolean) =>
                        handleGroupChange(group.id, checked)
                      }
                    />
                    <Label htmlFor={group.id} className={"cursor-pointer"}>
                      <span style={{ marginLeft: depth * 12 }}>
                        {group.name}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({getCustomerById(group.customerId)?.name})
                      </span>
                    </Label>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          {selectedGroups.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedGroups.length} group{selectedGroups.length !== 1 ? "s" : ""} selected
            </div>
          )}
        </div>
      )}

      {selectedCustomers.length > 0 && (
        <div className="space-y-2">
          <Label>Assign Tags</Label>
          {availableTags.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">
              No tags available for the selected customer(s)
            </div>
          ) : (
            <ScrollArea className="h-[200px] border rounded-md p-4">
              <div className="space-y-2">
                {availableTags.map((tag) => (
                  <div key={tag.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={tag.id}
                      checked={selectedTags.includes(tag.id)}
                      onCheckedChange={(checked: boolean) =>
                        handleTagChange(tag.id, checked)
                      }
                    />
                    <Label htmlFor={tag.id} className="cursor-pointer">
                      {tag.name}
                      <span className="text-xs text-muted-foreground ml-2">
                        ({getCustomerById(tag.customerId)?.name})
                      </span>
                    </Label>
                    <Badge variant={tag.type === "dynamic" ? "secondary" : "outline"} className="ml-auto text-xs">
                      {tag.type}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          {selectedTags.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedTags.length} tag{selectedTags.length !== 1 ? "s" : ""} selected
            </div>
          )}
        </div>
      )}

      {selectedCustomers.length > 0 && availableGroups.length === 0 && (
        <div className="text-sm text-muted-foreground py-2">
          No groups available for the selected customer(s)
        </div>
      )}
    </div>
  );
};
